// Yolutap — shared type definitions (Task 1)

export type LanguageKey = "ru" | "az" | "en";

export type LocalizedText = {
  ru: string;
  az?: string;
  en?: string;
};

export const ARCHETYPE_CODES = ["A", "I", "S", "K", "L", "E", "P", "B"] as const;
export type ArchetypeCode = (typeof ARCHETYPE_CODES)[number];

export type ScoresByArchetype = Record<ArchetypeCode, number>;

export const RIASEC_CODES = ["R", "I", "A", "S", "E", "H"] as const;
export type RiasecCode = (typeof RIASEC_CODES)[number];

export type AnswerOption = {
  answer_id: string;
  option_label: string;
  text: LocalizedText;
  archetype_code?: ArchetypeCode;
  riasec_code?: RiasecCode;
  environment_flag?: string;
  points: number;
};

export type Question = {
  question_id: string;
  display_number: number;
  age_group: string;
  test_version_id: string;
  block_id?: string;
  block_number?: number;
  block_title?: LocalizedText;
  question_kind?: "archetype" | "riasec" | "environment";
  question_type: string;
  is_required: boolean;
  question_text: LocalizedText;
  answers: AnswerOption[];
};

export type ParentObservationQuestion = Omit<
  Question,
  "block_id" | "block_number" | "block_title"
> & {
  form_version_id: string;
};

export type LocalizedQuestion = {
  question_id: string;
  display_number: number;
  block_id?: string;
  block_title?: string;
  question_text: string;
  answers: { answer_id: string; option_label: string; text: string }[];
  /** Optional question type. `"multiple_choice"` (default) renders an
   *  ABCD radio list. `"scale"` renders a 5-point Likert row with the
   *  scale_poles labels at the extremes. Used by the Path B 18-22 flow
   *  where block 6 questions are 1-5 scale not multi-choice. */
  question_type?: string;
  /** Endpoint labels for `question_type === "scale"` questions
   *  (e.g. {low: "Один", high: "Команда"}). */
  scale_poles?: { low: string; high: string };
};

export type ChildAnswer = { question_id: string; answer_id: string };

export type ParentRatingAnswer = { archetype_code: ArchetypeCode; rating: number };

export type ParentChoiceAnswer = { question_id: string; answer_id: string };

export type AlignmentLabel = "match" | "mismatch" | "hidden_potential" | "low_match" | "partial";

export type AgeGroupConfig = {
  age_group_id: string;
  label: LocalizedText;
  status: "active" | "coming_soon";
  question_count: number;
  test_version_id: string;
  parent_form_version_id: string;
  report_template_version_id: string;
  parent_form_required: boolean;
  scoring_model: {
    points_per_answer: number;
    mixed_profile_threshold: number;
    parent_high_threshold: number;
    parent_low_threshold: number;
  };
  available_languages: LanguageKey[];
};

export type ResultObject = {
  session_id?: string;
  age_group: string;
  test_version_id: string;
  parent_form_version_id?: string;
  report_template_version_id: string;
  language_key: LanguageKey;
  /** Plan B (10-11 + 16-18, June 2026): for these two bands `child_scores`
   *  holds the per-archetype-normalized values, scaled into [0, cap].
   *  All downstream consumers (renderer, ranking, mixed_profile, bar widths,
   *  parent-child alignment) read this field — so the same number is used
   *  everywhere a score is shown. For the other bands (12-13, 14-15, 18+)
   *  it stays the raw sum of `opt.points`. */
  child_scores: ScoresByArchetype;
  /** Plan B: raw archetype score before per-archetype normalization. Only
   *  populated for the bands that go through Plan B (10-11, 16-18). Used
   *  by the G3 safety-net warn, audit tooling, and rank tiebreak when two
   *  archetypes share the same scaled value. Other bands leave this
   *  undefined. */
  child_scores_raw?: ScoresByArchetype;
  total_score: number;
  primary_archetype: ArchetypeCode;
  secondary_archetype: ArchetypeCode;
  /** Чем выбран поддерживающий: баллом; при ничьей за второе место —
   *  сферой (14-18) или ответами родителя (10-13); «unresolved» — ничья
   *  осталась, отчёт печатает «так же близко: …» (also_close). */
  secondary_by?: "score" | "sphere" | "parent" | "unresolved";
  /** Ведущий и поддерживающий набрали поровну: «два ведущих архетипа поровну». */
  tie_lead?: boolean;
  /** Архетипы с тем же баллом, что у поддерживающего, когда ничью решить нечем. */
  also_close?: ArchetypeCode[];
  /** Ведущий, поддерживающий, затем остальные по рейтингу. */
  archetype_order?: ArchetypeCode[];
  mixed_profile: boolean;
  tie_group: ArchetypeCode[];
  combination_key: string;
  lowest_archetypes: ArchetypeCode[];
  parent_scores?: ScoresByArchetype;
  parent_child_alignment?: Record<ArchetypeCode, AlignmentLabel>;
  status: "complete" | "incomplete";
  riasec_scores?: Record<RiasecCode, number>;
  /** Совместимость: одна сфера → буква, две → [X, Y], широкие или нет данных → нет. */
  dominant_riasec?: RiasecCode | RiasecCode[];
  /** Сфера интересов 14-15 / 16-18 (sphere.ts): «одна / две / широкие». */
  sphere?: import("./sphere").SphereResult;
  environment_flags?: Record<string, string>;
  work_format?: {
    hire_vs_startup?: string;
    sector?: string;
    scale?: string;
    geography?: string;
    risk?: string;
  };
};
